/**
 * API Service
 * Centralized API client with authentication, in-flight GET dedup,
 * and a shared 401-refresh promise so a token-expiry storm collapses
 * to a single refresh + replay instead of N concurrent refreshes.
 */

import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Token getter function - will be set by AuthContext.
// Signature: (opts?: { forceRefresh?: boolean }) => Promise<string|null>
let getTokenFn = null;

export const setTokenGetter = (fn) => {
  getTokenFn = fn;
};

export const getToken = async (opts) => {
  if (getTokenFn) {
    try { return await getTokenFn(opts); } catch { return null; }
  }
  return null;
};

// ── In-flight GET dedup ─────────────────────────────────────────────
// Polling components and React StrictMode double-mounts can fire the same
// GET many times in rapid succession. Coalesce identical idempotent reads
// onto a single underlying request.
const inflightGets = new Map();

const dedupKey = (config) => {
  if (!config || (config.method || 'get').toLowerCase() !== 'get') return null;
  if (config.responseType && config.responseType !== 'json') return null; // never dedup blob downloads
  if (config._noDedup) return null;
  // Stable key — order-independent params via URLSearchParams.
  let params = '';
  if (config.params) {
    try {
      const sp = new URLSearchParams();
      for (const [k, v] of Object.entries(config.params)) sp.append(k, v);
      sp.sort();
      params = sp.toString();
    } catch { /* fall through */ }
  }
  return `${config.url}?${params}`;
};

// ── Shared 401 refresh ──────────────────────────────────────────────
// One refresh promise at a time; concurrent 401s all await it.
let inflightRefresh = null;

const sharedRefresh = () => {
  if (!inflightRefresh) {
    inflightRefresh = getToken({ forceRefresh: true })
      .finally(() => { inflightRefresh = null; });
  }
  return inflightRefresh;
};

// Request interceptor — attach token + register dedup
api.interceptors.request.use(
  async (config) => {
    if (getTokenFn) {
      try {
        const token = await getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (err) {
        console.error('Failed to get auth token:', err);
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor — single-flight refresh + retry on 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && getTokenFn && original && !original._authRetry) {
      original._authRetry = true;
      const token = await sharedRefresh().catch(() => null);
      if (token) {
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${token}`;
        return api(original);
      }
    }
    return Promise.reject(error);
  }
);

// Patch api.get / api.request to enable dedup. We can't use an interceptor here
// because interceptors run after the request is queued — dedup needs to happen
// at the call site so we never send two identical requests in parallel.
const originalGet = api.get.bind(api);
api.get = (url, config = {}) => {
  const key = dedupKey({ url, method: 'get', ...config });
  if (!key) return originalGet(url, config);

  const existing = inflightGets.get(key);
  if (existing) return existing;

  const p = originalGet(url, config).finally(() => {
    if (inflightGets.get(key) === p) inflightGets.delete(key);
  });
  inflightGets.set(key, p);
  return p;
};

export default api;
